import { useRef, useState } from "react"

import { Box, Button, ButtonText, HStack, Image, Text, VStack } from "wealthmode-frontend/gluestack/ui"

import { LOGO_SIZE, logoDataURL, rasterizeToLogo } from "@/lib/logo"

export function LogoField({
  value,
  inherited,
  onChange,
}: {
  value: string | null
  inherited: string | null
  onChange: (value: string | null) => void
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [error, setError] = useState<string | null>(null)

  const overridden = value !== null
  const shown = logoDataURL(overridden ? value : inherited)

  async function pick(file: File | undefined) {
    if (!file) {
      return
    }

    setError(null)

    try {
      onChange(await rasterizeToLogo(file))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <VStack className="gap-2">
      <Text className="text-sm font-medium text-foreground-primary">Logo</Text>
      <HStack className="gap-4 items-center">
        <Box className="h-24 w-24 items-center justify-center border border-border-medium">
          {shown ? (
            <Image
              source={{ uri: shown }}
              alt="Connector logo"
              className="h-20 w-20"
              resizeMode="contain"
            />
          ) : (
            <Text className="text-xs text-foreground-secondary">None</Text>
          )}
        </Box>
        <VStack className="gap-2">
          <HStack className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onPress={() => inputRef.current?.click()}
            >
              <ButtonText>Upload</ButtonText>
            </Button>
            <Button
              variant="outline"
              size="sm"
              isDisabled={!overridden}
              onPress={() => onChange(null)}
            >
              <ButtonText>Revert</ButtonText>
            </Button>
          </HStack>
          <Text className="text-xs text-foreground-secondary">
            {overridden
              ? `Overridden. Reverting restores the provider logo. Stored as a ${LOGO_SIZE}×${LOGO_SIZE} PNG.`
              : "Inherited from the provider."}
          </Text>
        </VStack>
      </HStack>
      {error ? <Text className="text-sm text-foreground-secondary">{error}</Text> : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg"
        style={{ display: "none" }}
        onChange={(event) => {
          void pick(event.target.files?.[0])
          event.target.value = ""
        }}
      />
    </VStack>
  )
}
